/*
 * Venda: catering add-ons in the cart drawer (snippets/cart-addons.liquid).
 *
 * All cart changes go through /cart/update.js with the theme's bundled
 * sections (Cart.sections), then through the theme's own Cart statics, so the
 * drawer re-renders exactly like a normal quantity change. The element is
 * replaced on every re-render, so the quantity a customer asked for is kept in
 * CartAddons.wanted until the cart matches it: taps made while a change is
 * saving are never lost.
 */
const UPDATE_DELAY = 350;

class CartAddons extends HTMLElement {
  static focus_target = null;
  static wanted = {};

  connectedCallback() {
    this.abort_controller = new AbortController();
    this.timers = {};

    customElements.whenDefined('cart-element').then(() => {
      this.Cart = customElements.get('cart-element');

      if (this.hasAttribute('data-orphan-keys')) {
        CartAddons.wanted = {};
        this.removeOrphans();
        return;
      }

      this.addListeners();
      this.resumeWanted();
      this.restoreFocus();
    });
  }

  disconnectedCallback() {
    this.abort_controller.abort();
    Object.values(this.timers).forEach(timer => clearTimeout(timer));
  }

  addListeners() {
    const { signal } = this.abort_controller;

    this.addEventListener('click', event => {
      const button = event.target.closest('.quantity-selector--minus, .quantity-selector--plus');
      if (!button) return;

      event.preventDefault();
      const row = button.closest('.cart--addon');
      const input = row.querySelector('.quantity-selector--input');
      const step = button.classList.contains('quantity-selector--plus') ? 1 : -1;
      const quantity = Math.max(0, (parseInt(input.value) || 0) + step);
      if (quantity === parseInt(input.value)) return;

      input.value = quantity;
      CartAddons.focus_target = { variant_id: row.dataset.variantId, control: step > 0 ? 'plus' : 'minus' };
      this.requestQuantity(row, quantity);
    }, { signal });

    this.addEventListener('change', event => {
      const input = event.target.closest('.quantity-selector--input');
      if (!input) return;

      const row = input.closest('.cart--addon');
      const quantity = Math.max(0, parseInt(input.value) || 0);
      input.value = quantity;
      this.requestQuantity(row, quantity);
    }, { signal });
  }

  // after a re-render, carry on with any quantity asked for while the last change was saving
  resumeWanted() {
    this.querySelectorAll('.cart--addon').forEach(row => {
      const wanted = CartAddons.wanted[row.dataset.variantId];
      if (wanted === undefined) return;

      if (wanted === parseInt(row.dataset.quantity)) {
        delete CartAddons.wanted[row.dataset.variantId];
      } else {
        row.querySelector('.quantity-selector--input').value = wanted;
        this.requestQuantity(row, wanted);
      }
    });
  }

  requestQuantity(row, quantity) {
    const { variantId: variant_id } = row.dataset;
    CartAddons.wanted[variant_id] = quantity;

    // tells assets/cart-app-sync.js to hold off re-rendering the drawer
    this.setAttribute('data-pending', '');

    // a change is already saving: the re-render picks this up in resumeWanted()
    if (row.getAttribute('aria-busy') === 'true') return;

    clearTimeout(this.timers[variant_id]);
    this.timers[variant_id] = setTimeout(() => this.updateQuantity(row), UPDATE_DELAY);
  }

  async updateQuantity(row) {
    const { lineKey: line_key, quantity: current, variantId: variant_id } = row.dataset;
    const quantity = CartAddons.wanted[variant_id];
    delete this.timers[variant_id];

    if (quantity === undefined || quantity === parseInt(current)) {
      delete CartAddons.wanted[variant_id];
      this.clearPending();
      return;
    }

    // in the cart: change that line by key (line numbers shift when apps add fee lines)
    // not in the cart: /cart/update.js adds it by variant id
    const target = line_key || variant_id;

    row.setAttribute('aria-busy', true);

    try {
      await this.updateCart({ [target]: quantity });
      // normally this element is replaced by the re-render; if it wasn't, unlock it
      // and send anything asked for while saving
      if (!this.isConnected) return;
      row.dataset.quantity = quantity;
      row.removeAttribute('aria-busy');
      const wanted = CartAddons.wanted[variant_id];
      if (wanted !== undefined && wanted !== quantity) {
        this.requestQuantity(row, wanted);
      } else {
        delete CartAddons.wanted[variant_id];
        this.clearPending();
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      delete CartAddons.wanted[variant_id];
      row.querySelector('.quantity-selector--input').value = current;
      row.removeAttribute('aria-busy');
      this.clearPending();
    }
  }

  clearPending() {
    const busy = this.querySelector('.cart--addon[aria-busy="true"]');
    if (!busy && Object.keys(this.timers).length === 0) this.removeAttribute('data-pending');
  }

  async removeOrphans() {
    const updates = {};
    JSON.parse(this.dataset.orphanKeys).forEach(key => (updates[key] = 0));

    try {
      await this.updateCart(updates);
    } catch {}
  }

  async updateCart(updates) {
    const response = await fetch(`${Shopify.routes.cart_update}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates, sections: this.Cart.sections }),
      signal: this.abort_controller.signal
    });
    if (!response.ok) throw new Error(response.statusText);

    const { items, item_count, sections } = await response.json();
    this.Cart.fillBasket(items);
    this.Cart.updateTotals(item_count);
    this.Cart.updateAllHtml(sections);
  }

  restoreFocus() {
    const target = CartAddons.focus_target;
    CartAddons.focus_target = null;
    if (!target) return;

    const row = this.querySelector(`.cart--addon[data-variant-id='${target.variant_id}']`);
    row?.querySelector(`.quantity-selector--${target.control}`)?.focus({ preventScroll: true });
  }
}

customElements.define('cart-addons', CartAddons);
